/**
 * Read-only tenant portal rent ledger (ZRF-FIN-012).
 * Flag-gated via REACT_APP_FINANCIALS_ENABLED. No write actions.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  downloadTenantPortalReceiptPdf,
  formatCents,
  getTenantPortalLedger,
  heldDepositCents
} from './api';

function TenantPortalLedger({ tenantId, propertyId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !propertyId) {
      setError('Missing tenant or property for ledger');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ledger = await getTenantPortalLedger(tenantId);
      setData(ledger);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError(null);
        setData(null);
      } else {
        setError(
          err?.response?.data?.error || err?.message || 'Failed to load ledger'
        );
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadReceipt = async (receiptId, receiptNumber) => {
    try {
      const blob = await downloadTenantPortalReceiptPdf(tenantId, receiptId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${receiptNumber || receiptId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* ignore — receipt may not exist yet */
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Rent ledger is not available yet. Your landlord will create it from your
        lease when ready.
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No rent ledger yet for this tenancy.
      </Alert>
    );
  }

  const balance = data.balance || {};
  const nextDue = (data.schedules || []).find(
    (s) =>
      s.status !== 'paid' &&
      s.status !== 'voided' &&
      s.status !== 'cancelled'
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        My Rent &amp; Payments
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Balance due:{' '}
        <strong>
          {formatCents(balance.balanceDueCents, balance.currency)}
        </strong>
        {balance.creditBalanceCents > 0 && (
          <>
            {' '}
            · Credit: {formatCents(balance.creditBalanceCents, balance.currency)}
          </>
        )}
        {nextDue && (
          <>
            <br />
            Next due: {nextDue.dueDate} —{' '}
            {formatCents(nextDue.amountRemainingCents ?? nextDue.amountCents, nextDue.currency)}
          </>
        )}
        <br />
        Deposits held:{' '}
        {formatCents(balance.depositsHeldCents, balance.currency)}
      </Alert>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Read-only view. Contact your landlord to record payments or update
        deposits.
      </Typography>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        Recent activity
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data.entries || []).slice(0, 20).map((e) => {
            const amount =
              Number(e.debitCents || 0) > 0
                ? formatCents(e.debitCents, e.currency)
                : Number(e.creditCents || 0) > 0
                  ? `−${formatCents(e.creditCents, e.currency)}`
                  : '';
            return (
              <TableRow key={e.id}>
                <TableCell>{e.entryDate}</TableCell>
                <TableCell>{e.entryType}</TableCell>
                <TableCell>{e.description}</TableCell>
                <TableCell align="right">{amount}</TableCell>
              </TableRow>
            );
          })}
          {(data.entries || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  No ledger activity yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Receipts
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Number</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">PDF</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data.receipts || []).map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.receiptNumber}</TableCell>
              <TableCell>{r.paymentDate}</TableCell>
              <TableCell align="right">
                {formatCents(r.amountCents, r.currency)}
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadReceipt(r.id, r.receiptNumber)}
                >
                  PDF
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {(data.receipts || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  No receipts yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Deposits
      </Typography>
      <Stack spacing={0.5}>
        {(data.deposits || []).map((d) => (
          <Typography key={d.id} variant="body2">
            {d.depositType}: {d.status} · held{' '}
            {formatCents(heldDepositCents(d), d.currency)}
          </Typography>
        ))}
        {(data.deposits || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No deposits on file.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default TenantPortalLedger;
